/**
 * profileApi — the one fetch for `GET /api/profile/me` and `GET /api/profile/:userId`.
 *
 * Plain `fetch` with a typed failure reason, no cache layer: a profile is opened by hand a few
 * times a session, so there is nothing to cache and nothing to invalidate.
 *
 * Two rules it exists to keep in one place:
 *
 *  - **Every path goes through `apiPath()`.** Inside the Discord Activity iframe a request without
 *    the `/.proxy` prefix never leaves the frame, and a profile that only works on the web is a
 *    profile that works for half the players.
 *  - **The body is parsed, not cast.** `ProfileViewSchema.safeParse` means a server one deploy
 *    ahead (or a proxy handing back an HTML error page with a 200) shows "Could not reach the
 *    records" rather than crashing the sheet on `profile.matches.map`.
 *
 * Auth is the session JWT — the same `Authorization: Bearer` header `ColyseusDriver` and the error
 * reporter send, read from the store when it is up and from localStorage `sn.token` otherwise, so
 * the sheet still loads on a surface that mounts before identity resolves.
 */
import { type ProfileView, ProfileViewSchema } from '@spades/shared';
import { apiPath } from '../bridge/embedded';
import { readStoredToken } from '../bridge/identity';
import { store } from '../store/store';

/**
 * Why the caller cares which failure it was. Each one has its own copy in the sheet, because
 * "something went wrong" is the least useful thing any of these could say:
 *
 *  - `unauth`    → 401. No session at all; nothing to show and nothing to retry.
 *  - `forbidden` → 403. Not this player's profile and not someone at their table. Deliberately
 *                  opaque on the server, so the client must not guess that the user exists.
 *  - `notfound`  → 404. A real, expected state: nobody has finished a match under this id yet.
 *  - `nodb`      → 503. The server runs without `DATABASE_URL`. Records are off, the game is fine.
 *  - `offline`   → anything else, including a dead socket and an unparseable body.
 */
export type ProfileFailure = 'unauth' | 'forbidden' | 'notfound' | 'nodb' | 'offline';

export type ProfileResult = { ok: true; profile: ProfileView } | { ok: false; reason: ProfileFailure };

/** Last 20 by default; the route clamps anything else to 1..50. */
export const PROFILE_MATCH_LIMIT = 20;

/** The session JWT: the store first (it is authoritative), the stored copy as the fallback. */
function bearer(): string | null {
  try {
    const fromStore = store.getState().session.token;
    if (fromStore) return fromStore;
  } catch {
    /* store not built yet (tools build, early boot) */
  }
  return readStoredToken();
}

/**
 * One player's profile. `'me'` is the caller's own — the only id a client should ever need to
 * name, since every other profile it can open belongs to somebody sitting at the same table.
 */
export async function fetchProfile(id: string | 'me', limit: number = PROFILE_MATCH_LIMIT): Promise<ProfileResult> {
  const token = bearer();
  const path = apiPath(`/api/profile/${encodeURIComponent(id)}?limit=${limit}`);

  let res: Response;
  try {
    res = await fetch(path, {
      cache: 'no-store',
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    return { ok: false, reason: 'offline' };
  }

  if (res.status === 401) return { ok: false, reason: 'unauth' };
  if (res.status === 403) return { ok: false, reason: 'forbidden' };
  if (res.status === 404) return { ok: false, reason: 'notfound' };
  if (res.status === 503) return { ok: false, reason: 'nodb' };
  if (!res.ok) return { ok: false, reason: 'offline' };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, reason: 'offline' };
  }

  const parsed = ProfileViewSchema.safeParse(body);
  if (!parsed.success) {
    console.warn('[profile] unexpected payload', parsed.error);
    return { ok: false, reason: 'offline' };
  }
  return { ok: true, profile: parsed.data };
}

/**
 * Human text for a failed load. One wording per reason, here rather than at each call site, so the
 * lobby and the in-table sheet cannot drift into saying different things about the same 403.
 */
export function profileFailureText(reason: ProfileFailure): string {
  switch (reason) {
    case 'unauth':
      return 'Sign in to see your record.';
    case 'forbidden':
      return 'Only players at this table can open a profile.';
    case 'notfound':
      return 'Play one match to start your record.';
    case 'nodb':
      return 'Records are offline right now.';
    default:
      return 'Could not reach the records.';
  }
}
